import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { useCRM } from '../context/CRMContext';
import { useCRMData } from '../context/CRMDataContext';
import { useTeam } from '../context/TeamContext';
import BaseModal from '../components/modals/BaseModal';
import CompanyForm from '../components/forms/CompanyForm';
import FilterPanel from '../components/common/FilterPanel';
import DuplicateCompanyPanel from '../components/companies/DuplicateCompanyPanel';

const normalize = (value) => String(value || '')
  .toLowerCase()
  .replace(/[^\p{L}\p{N}]/gu, '');

const normalizeDomain = (value) => String(value || '')
  .toLowerCase()
  .replace(/^https?:\/\//, '')
  .replace(/^www\./, '')
  .split('/')[0]
  .trim();

const Companies = () => {
  const { mergeCompanies } = useCRM();
  const { users } = useTeam();
  const { companies } = useCRMData();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [activeFilters, setActiveFilters] = useState({});

  const availableFilters = [
    { key: 'name', label: 'Navn', type: 'text' },
    { key: 'industry', label: 'Bransje', type: 'text' },
    { key: 'city', label: 'By', type: 'text' },
    { key: 'status', label: 'Status', type: 'select', options: [
      { value: 'Lead', label: 'Lead' },
      { value: 'Prospect', label: 'Prospekt' },
      { value: 'Customer', label: 'Kunde' },
      { value: 'Partner', label: 'Partner' }
    ]},
    { key: 'owner', label: 'Ansvarlig', type: 'select', options: users.map(u => ({ value: u.user_id, label: u.name })) },
    { key: 'gdpr_status', label: 'GDPR Status', type: 'select', options: [
      { value: 'Samtykke', label: 'Samtykke' },
      { value: 'Berettiget interesse', label: 'Berettiget interesse' },
      { value: 'Usikker', label: 'Usikker' },
      { value: 'Blokker kontakt', label: 'Blokker kontakt' }
    ]},
    { key: 'source', label: 'Kilde', type: 'text' },
    { key: 'tag', label: 'Tag', type: 'text' }
  ];

  const quickFilters = [
    { label: 'Mine selskaper', isActive: activeFilters.owner === 'u1', onClick: () => setActiveFilters(prev => prev.owner === 'u1' ? {} : { owner: 'u1' }) },
    { label: 'Kunder', isActive: activeFilters.status === 'Customer', onClick: () => setActiveFilters(prev => prev.status === 'Customer' ? {} : { status: 'Customer' }) },
    { label: 'Leads', isActive: activeFilters.status === 'Lead', onClick: () => setActiveFilters(prev => prev.status === 'Lead' ? {} : { status: 'Lead' }) }
  ];

  const filteredCompanies = useMemo(() => {
    return companies.filter(company => {
      for (const [key, value] of Object.entries(activeFilters)) {
        if (!value) continue;
        const filterValue = value.toLowerCase();
        if (key === 'name' && !company.name.toLowerCase().includes(filterValue)) return false;
        if (key === 'city' && !company.city?.toLowerCase().includes(filterValue)) return false;
        if (key === 'industry' && !company.industry?.toLowerCase().includes(filterValue)) return false;
        if (key === 'source' && !company.source?.toLowerCase().includes(filterValue)) return false;
        if (key === 'owner' && company.owner_user_id !== value) return false;
        if (key === 'gdpr_status' && company.gdpr_status !== value) return false;
        if (key === 'status' && company.status !== value) return false;
        if (key === 'tag') {
          const hasTag = company.tags && company.tags.some(t => t.text.toLowerCase().includes(filterValue));
          if (!hasTag) return false;
        }
      }
      return true;
    });
  }, [companies, activeFilters]);

  const duplicateGroups = useMemo(() => {
    const groups = [];
    const seen = new Set();

    companies.forEach((company, index) => {
      if (seen.has(company.company_id) || company.status === 'Merged') return;

      for (let nextIndex = index + 1; nextIndex < companies.length; nextIndex += 1) {
        const other = companies[nextIndex];
        if (other.status === 'Merged') continue;

        const sameOrgNumber = normalize(company.org_number) &&
          normalize(company.org_number) === normalize(other.org_number);
        const sameName = normalize(company.name) &&
          normalize(company.name) === normalize(other.name);
        const sameDomain = normalizeDomain(company.website) &&
          normalizeDomain(company.website) === normalizeDomain(other.website);
        const sameCity = normalize(company.city) &&
          normalize(company.city) === normalize(other.city);

        const score = sameOrgNumber ? 100 : sameName && sameDomain ? 95 : sameName && sameCity ? 85 : sameDomain ? 75 : 0;

        if (score >= 75) {
          const key = [company.company_id, other.company_id].sort().join('-');
          groups.push({
            key,
            companies: [company, other],
            score,
            reason: sameOrgNumber ? 'Samme organisasjonsnummer' : sameName && sameDomain ? 'Samme navn og nettsted' : sameName ? 'Samme navn og by' : 'Samme nettsted'
          });
          seen.add(company.company_id);
          seen.add(other.company_id);
          break;
        }
      }
    });

    return groups.sort((a, b) => b.score - a.score);
  }, [companies]);

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Customer': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'Lead': return 'bg-blue-50 text-blue-700 border-blue-100';
      case 'Prospect': return 'bg-amber-50 text-amber-700 border-amber-100';
      case 'Partner': return 'bg-indigo-50 text-indigo-700 border-indigo-100';
      case 'Merged': return 'bg-slate-100 text-slate-600 border-slate-200';
      default: return 'bg-slate-50 text-slate-600 border-slate-100';
    }
  };

  const handleMergeGroup = async ({ primaryId, duplicateId }) => {
    const primary = companies.find(company => company.company_id === primaryId);
    const duplicate = companies.find(company => company.company_id === duplicateId);

    if (!primary || !duplicate) return;

    const confirmed = window.confirm(
      `Sammenslå «${duplicate.name}» med «${primary.name}»?\n\n` +
      'Kontakter og deals flyttes til hovedselskapet. Ingen data slettes.'
    );

    if (!confirmed) return;

    const result = await mergeCompanies(duplicateId, primaryId);

    if (!result.success) {
      window.alert(result.error?.message || 'Sammenslåingen kunne ikke gjennomføres.');
      return;
    }

    window.alert(
      `Selskapene er slått sammen. ${result.data?.moved_contacts || 0} kontakter og ` +
      `${result.data?.moved_deals || 0} deals ble flyttet.`
    );
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-slate-200">
        <div className="space-y-2">
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Selskaper</h1>
          <p className="text-slate-500 font-medium text-lg">Administrer din portefølje av selskaper og relasjoner.</p>
        </div>
        <button 
          onClick={() => { setEditingCompany(null); setIsModalOpen(true); }}
          className="flex items-center gap-2 px-8 py-4 bg-slate-900 text-white rounded-[1.25rem] font-black text-xs uppercase tracking-[0.2em] hover:bg-slate-800 transition-all shadow-xl shadow-slate-200"
        >
          <SafeIcon icon="➕" className="w-5 h-5" /> Nytt Selskap
        </button>
      </div>

      <FilterPanel availableFilters={availableFilters} activeFilters={activeFilters} onFilterChange={setActiveFilters} quickFilters={quickFilters} />

      <DuplicateCompanyPanel
        groups={duplicateGroups}
        onOpenCompany={(company) => {
          setEditingCompany(company);
          setIsModalOpen(true);
        }}
        onMergeGroup={handleMergeGroup}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredCompanies.map((company, idx) => (
          <motion.div
            key={company.company_id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
            onClick={() => navigate(`/companies/${company.company_id}`)}
            className="group bg-white rounded-[2.5rem] border border-slate-200 p-8 hover:border-primary-300 shadow-sm hover:shadow-2xl hover:shadow-primary-500/10 transition-all duration-500 cursor-pointer relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50 rounded-bl-[5rem] -mr-16 -mt-16 group-hover:bg-primary-50 transition-colors duration-500" />
            
            <div className="relative flex items-start justify-between mb-8">
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center border border-slate-100 shadow-sm overflow-hidden group-hover:scale-110 transition-transform duration-500">
                  {company.logo ? (
                    <img src={company.logo} alt={company.name} className="w-full h-full object-contain p-2" />
                  ) : (
                    <SafeIcon icon="🏢" className="text-3xl text-slate-300" />
                  )}
                </div>
                <div>
                  <h3 className="text-xl font-black text-slate-900 leading-tight group-hover:text-primary-600 transition-colors line-clamp-1">{company.name}</h3>
                  <div className={`inline-flex px-2.5 py-1 rounded-lg border text-[10px] font-black uppercase tracking-widest mt-2 ${getStatusStyle(company.status)}`}>
                    {company.status || 'Lead'}
                  </div>
                </div>
              </div>
              <button 
                onClick={(e) => { e.stopPropagation(); setEditingCompany(company); setIsModalOpen(true); }}
                className="p-3 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-xl transition-all opacity-0 group-hover:opacity-100"
              >
                <SafeIcon icon="✏️" className="w-5 h-5" />
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {company.tags?.map((tag, i) => (
                <span key={i} className="px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-[10px] font-black uppercase tracking-widest border border-slate-200/50">
                  {tag.text}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-4 pt-6 border-t border-slate-100">
              <div className="space-y-1">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                  <SafeIcon icon="💼" className="w-3 h-3" /> Bransje
                </p>
                <p className="text-sm font-bold text-slate-700 truncate">{company.industry}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                  <SafeIcon icon="📍" className="w-3 h-3" /> Sted
                </p>
                <p className="text-sm font-bold text-slate-700 truncate">{company.city}</p>
              </div>
            </div>

            {company.website && (
              <div className="mt-6 flex items-center justify-between">
                <div className="flex items-center gap-2 text-primary-600">
                  <SafeIcon icon="🌐" className="w-4 h-4" /> 
                  <span className="text-xs font-black uppercase tracking-widest truncate max-w-[150px]">{company.website.replace('https://', '')}</span>
                </div>
                <div className="w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center text-white translate-x-12 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-500 shadow-lg shadow-slate-200">
                  <SafeIcon icon="➡️" className="w-4 h-4" />
                </div>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      <BaseModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingCompany ? "Rediger Selskap" : "Opprett Selskap"}>
        <CompanyForm company={editingCompany} onClose={() => setIsModalOpen(false)} />
      </BaseModal>

      {filteredCompanies.length === 0 && (
        <div className="py-24 text-center bg-white rounded-[3rem] border-2 border-dashed border-slate-200">
          <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon="🏢" className="w-10 h-10 text-slate-300" />
          </div>
          <h3 className="text-2xl font-black text-slate-900 mb-2">Ingen selskaper funnet</h3>
          <p className="text-slate-500 font-medium max-w-xs mx-auto mb-8">Prøv å endre filtrene eller legg til et nytt selskap i din portefølje.</p>
          <button onClick={() => setActiveFilters({})} className="px-8 py-3 bg-slate-900 text-white rounded-2xl font-bold shadow-xl shadow-slate-200 hover:scale-105 transition-transform">
            Nullstill filtre
          </button>
        </div>
      )}
    </div>
  );
};

export default Companies;